import EventIcon from '@mui/icons-material/Event';
import UpdateIcon from '@mui/icons-material/Update';
import { SvgIcon, SvgIconProps } from '@mui/material';
import { ReactElement } from 'react';
import { ReactComponent as WorkInProgressSvgIcon } from '../../../../assets/images/icons/work-in-progress.svg';
import { L3ServiceStatus, L3ServiceViewModel } from '../../model';
import { MaintenanceWork, MaintenanceWorkStatus } from '../../model/MaintenanceWork.model';

export interface L3ServiceStatusIconModel {
  icon: L3ServiceViewModelIcon;
  iconColor: L3ServiceViewModelIconColor;
  backgroundColor: L3ServiceViewModelBackgroundColor;
  maintenanceProgressBorderLineStyle: L3ServiceViewModelIconMaintenanceProgressBorderLineStyle;
  maintenanceProgressBorderColor: L3ServiceViewModelMaintenanceProgressBorderColor;
  workProgress: number;
}

export type L3ServiceViewModelIconMaintenanceProgressBorderLineStyle = 'solid' | 'dashed';

export enum L3ServiceViewModelBackgroundColor {
  Orange = 'rgba(251, 146, 60, 0.5)',
  Purple = 'rgba(107, 53, 132, 0.4)',
  Red = 'rgba(255, 231, 230, 1)',
  Green = 'rgba(223, 251, 229, 1)',
  Gray = 'rgba(127, 139, 147, 0.2)'
}

export enum L3ServiceViewModelMaintenanceProgressBorderColor {
  Orange = 'rgba(251, 146, 60, 0.9)',
  Purple = 'rgba(107, 53, 132, 0.7)',
  Red = '#FF5757',
  Green = '#73D18D',
  Gray = '#C3C2C2'
}

export enum L3ServiceViewModelIconColor {
  Red = '#FF5757',
  Green = '#008C27',
  Gray = '#3F3F3F',
  Black = 'black'
}

export enum L3ServiceViewModelIcon {
  WorkInProgress = 'WORK_IN_PROGRESS',
  WorkScheduled = 'WORK_SCHEDULED',
  Archived = 'ARCHIVED',
  Empty = 'EMPTY'
}

export const getStatusIcon = (l3Service: L3ServiceViewModel): L3ServiceViewModelIcon => {
  const isAnyMaintenanceWorkOngoing = isAnyMaintenanceWorkInStatus(l3Service.maintenanceWorks, MaintenanceWorkStatus.Ongoing);
  const isAnyMaintenanceWorkPlanned = isAnyMaintenanceWorkInStatus(l3Service.maintenanceWorks, MaintenanceWorkStatus.Planned);
  if (isAnyMaintenanceWorkOngoing) {
    return L3ServiceViewModelIcon.WorkInProgress;
  } else if (isAnyMaintenanceWorkPlanned) {
    return L3ServiceViewModelIcon.WorkScheduled;
  } else if (l3Service.status === L3ServiceStatus.Archived) {
    return L3ServiceViewModelIcon.Archived;
  } else {
    return L3ServiceViewModelIcon.Empty;
  }
};

export const getStatusIconColor = (l3Service: L3ServiceViewModel): L3ServiceViewModelIconColor => {
  const isWorkInProgress = isAnyMaintenanceWorkInProgress(l3Service.maintenanceWorks);
  switch (l3Service.status) {
    case L3ServiceStatus.Pending:
      return L3ServiceViewModelIconColor.Black;
    case L3ServiceStatus.Ready:
      return L3ServiceViewModelIconColor.Black;
    case L3ServiceStatus.Unavailable:
      return isWorkInProgress ? L3ServiceViewModelIconColor.Black : L3ServiceViewModelIconColor.Red;
    case L3ServiceStatus.Available:
      return isWorkInProgress ? L3ServiceViewModelIconColor.Black : L3ServiceViewModelIconColor.Green;
    case L3ServiceStatus.Archived:
      return isWorkInProgress ? L3ServiceViewModelIconColor.Black : L3ServiceViewModelIconColor.Gray;
  }
};

export const getStatusIconBackgroundColor = (l3Service: L3ServiceViewModel): L3ServiceViewModelBackgroundColor => {
  switch (l3Service.status) {
    case L3ServiceStatus.Pending:
      return L3ServiceViewModelBackgroundColor.Orange;
    case L3ServiceStatus.Ready:
      return L3ServiceViewModelBackgroundColor.Purple;
    case L3ServiceStatus.Unavailable:
      return L3ServiceViewModelBackgroundColor.Red;
      case L3ServiceStatus.Available:
        return L3ServiceViewModelBackgroundColor.Green;
    case L3ServiceStatus.Archived:
      return L3ServiceViewModelBackgroundColor.Gray;
  }
};

export const getStatusIconMaintenanceProgressBorderLineStyle = (l3Service: L3ServiceViewModel): L3ServiceViewModelIconMaintenanceProgressBorderLineStyle =>
  isAnyMaintenanceWorkInProgress(l3Service.maintenanceWorks) ? 'dashed' : 'solid';

export const getStatusIconMaintenanceProgressBorderColor = (l3Service: L3ServiceViewModel): L3ServiceViewModelMaintenanceProgressBorderColor => {
  switch (l3Service.status) {
    case L3ServiceStatus.Pending:
      return L3ServiceViewModelMaintenanceProgressBorderColor.Orange;
    case L3ServiceStatus.Ready:
      return L3ServiceViewModelMaintenanceProgressBorderColor.Purple;
    case L3ServiceStatus.Unavailable:
      return L3ServiceViewModelMaintenanceProgressBorderColor.Red;
    case L3ServiceStatus.Available:
      return L3ServiceViewModelMaintenanceProgressBorderColor.Green;
    case L3ServiceStatus.Archived:
      return L3ServiceViewModelMaintenanceProgressBorderColor.Gray;
  }
};

export const getCurrentMaintenanceWorkInProgress = (works: MaintenanceWork[]): MaintenanceWork | null => {
  const now = Date.now();
  return works.find(work => new Date(work.startTime).getTime() <= now && new Date(work.endTime).getTime() >= now) || null;
};

export const isAnyMaintenanceWorkInProgress = (works: MaintenanceWork[]): boolean => {
  return Boolean(getCurrentMaintenanceWorkInProgress(works));
};

export const isAnyMaintenanceWorkScheduledInFuture = (works: MaintenanceWork[]): boolean => {
  return works.some(work => new Date(work.startTime).getTime() >= Date.now());
};

export const calculateMaintenanceWorkPercentageProgress = (works: MaintenanceWork[]): number => {
  if (works.length) {
    const now = Date.now();
    if (isAnyMaintenanceWorkInProgress(works)) {
      const currentWorkInProgress = getCurrentMaintenanceWorkInProgress(works);
      if (currentWorkInProgress) {
        const currentWorkInProgressStartTime = new Date(currentWorkInProgress.startTime).getTime();
        const currentWorkInProgressEndTime = new Date(currentWorkInProgress.endTime).getTime();
        if (now > currentWorkInProgressEndTime) {
          return 100;
        }
        const duration = currentWorkInProgressEndTime - currentWorkInProgressStartTime;
        if (!duration) {
          return 100;
        }
        return Number((((now - currentWorkInProgressStartTime) / duration) * 100).toFixed(2));
      }
      return 0;
    } else {
      return 0;
    }
  }
  return 0;
};

export const getIconComponent = (iconConfig: L3ServiceStatusIconModel): ReactElement => {
  const iconProps: SvgIconProps = {
    fontSize: 'large',
    sx: { position: 'absolute', top: 9, left: 9, color: iconConfig.iconColor },
  };
  switch (iconConfig.icon) {
    case L3ServiceViewModelIcon.WorkInProgress: {
      return <SvgIcon {...iconProps}>
        <WorkInProgressSvgIcon />
      </SvgIcon>;
    }
    case L3ServiceViewModelIcon.WorkScheduled: {
      return <UpdateIcon {...iconProps} />;
    }
    case L3ServiceViewModelIcon.Archived: {
      return <EventIcon {...iconProps} />;
    }
    case L3ServiceViewModelIcon.Empty: {
      return <></>;
    }
  }
};

const isAnyMaintenanceWorkInStatus = (
  maintenanceWorks: MaintenanceWork[],
  status: MaintenanceWorkStatus
): boolean => {
  const ongoingMaintenanceWork = maintenanceWorks
    .find(maintenanceWork => maintenanceWork.status === status);
  return ongoingMaintenanceWork !== undefined;
};